import React, { useEffect, useMemo, useState, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Card } from '../../components/ui/Card';
import { LineChart, BarChart, DoughnutChart } from '../../components/charts';
import {
  Loader2, TrendingUp, TrendingDown, DollarSign, MousePointerClick,
  ShoppingCart, Download, BarChart3, Users, Smartphone, Activity
} from 'lucide-react';

const API = 'https://api.glowytics.com/api';
const DEMO_BRAND = 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4';
const palette = ['#3b82f6','#22c55e','#f59e0b','#a855f7','#ef4444','#06b6d4','#10b981','#eab308','#8b5cf6','#f97316'];

const toYmd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const fmtCurrency = (n) => '₺' + Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
const fmtPct = (n) => `${Number(n || 0).toFixed(2)}%`;

// ─── Skeleton ───────────────────────────────────────────
const Skeleton = ({ w = 'w-20', h = 'h-7' }) => (
  <span className={`inline-block ${w} ${h} bg-gray-100 animate-pulse rounded`} />
);

// ─── KPI Card ───────────────────────────────────────────
function KpiCard({ label, value, icon: Icon, iconBg, change, loading: isLoading, prefix, suffix }) {
  const changeColor = change > 0 ? 'text-green-600' : change < 0 ? 'text-red-500' : 'text-gray-400';
  const ChangeIcon = change > 0 ? TrendingUp : change < 0 ? TrendingDown : null;
  return (
    <Card className="p-5 rounded-2xl hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{label}</p>
          <p className="text-2xl font-bold text-gray-900 truncate">
            {isLoading ? <Skeleton /> : <>{prefix}{value}{suffix}</>}
          </p>
          {!isLoading && change !== undefined && change !== null && (
            <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${changeColor}`}>
              {ChangeIcon && <ChangeIcon className="w-3.5 h-3.5" />}
              <span>{change > 0 ? '+' : ''}{fmtPct(change)}</span>
              <span className="text-gray-400 font-normal">vs prev period</span>
            </div>
          )}
        </div>
        <div className={`w-10 h-10 rounded-xl ${iconBg || 'bg-gray-100'} flex items-center justify-center flex-shrink-0`}>
          {Icon && <Icon className="w-5 h-5 text-gray-600" />}
        </div>
      </div>
    </Card>
  );
}

// ─── Mini stat card for DAU/WAU/MAU ─────────────────────
function ActiveUserCard({ label, value, sublabel, loading: isLoading, color }) {
  return (
    <Card className="p-4 rounded-2xl hover:shadow-md transition-shadow">
      <div className="flex items-center gap-3">
        <div className={`w-2 h-10 rounded-full ${color}`} />
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
          <p className="text-xl font-bold text-gray-900">
            {isLoading ? <Skeleton w="w-16" h="h-6" /> : fmt(value)}
          </p>
          {sublabel && !isLoading && (
            <p className="text-xs text-gray-400 mt-0.5">{sublabel}</p>
          )}
        </div>
      </div>
    </Card>
  );
}

// ─── Month label helper ─────────────────────────────────
const monthLabel = (ym) => {
  const d = new Date(`${ym}-01T00:00:00Z`);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

// ─── Demo dummy data generator ──────────────────────────
function generateDemoData(startYmd, endYmd) {
  let rangeStart = startYmd ? new Date(`${startYmd}T00:00:00`) : null;
  let rangeEnd = endYmd ? new Date(`${endYmd}T00:00:00`) : null;
  if (!rangeStart || !rangeEnd) {
    const d = new Date(); d.setDate(d.getDate() - 30);
    rangeStart = d; rangeEnd = new Date();
  }
  if (rangeStart > rangeEnd) { const t = rangeStart; rangeStart = rangeEnd; rangeEnd = t; }

  const days = [];
  const cursor = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate());
  const endDate = new Date(rangeEnd.getFullYear(), rangeEnd.getMonth(), rangeEnd.getDate());
  while (cursor <= endDate) { days.push(new Date(cursor)); cursor.setDate(cursor.getDate() + 1); }

  const daily = [], raw = [], auDaily = [];
  days.forEach((d, idx) => {
    const ds = toYmd(d);
    const base = 1200 + (idx % 7) * 150 + Math.round(Math.sin(idx / 3) * 80);
    const inst = Math.round(base * 0.45);
    const pr = 0.07 + ((idx % 5) * 0.004);
    const purch = Math.round(base * pr);
    const rev = purch * (900 + (idx % 4) * 120);
    daily.push({ day: ds, sessions: base, installs: inst, purchases: purch, all_revenue: rev });
    [
      { channel: 'Facebook Ads', source_network: 'Facebook', campaign: 'Prospecting - Broad', os_name: 'iOS', platform: 'iOS App', partner: 'Partner A', f: 0.5 },
      { channel: 'Google Ads', source_network: 'Google', campaign: 'Performance Max', os_name: 'Android', platform: 'Android App', partner: 'Partner B', f: 0.3 },
      { channel: 'Apple Search Ads', source_network: 'Apple Search Ads', campaign: 'Brand - Exact', os_name: 'iOS', platform: 'iOS App', partner: 'Partner A', f: 0.2 },
    ].forEach(r => raw.push({
      ...r, day: ds,
      sessions: Math.round(base * r.f), purchase_events: Math.round(purch * r.f),
      installs: Math.round(inst * r.f), all_revenue: Math.round(rev * r.f),
    }));
    auDaily.push({ date: ds, daus: 70000 + Math.round(Math.sin(idx / 5) * 8000), waus: 280000 + idx * 200, maus: 800000 + idx * 500 });
  });

  const monthly = [];
  const me = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
  for (let i = 5; i >= 0; i--) {
    const m = new Date(me.getFullYear(), me.getMonth() - i, 1);
    const ym = `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}-01`;
    const mi = (m.getFullYear() * 12) + m.getMonth();
    const bs = 42000 + (i * 2500) + ((mi % 3) * 1200);
    const ins = Math.round(bs * 0.46);
    const pu = Math.round(bs * (0.075 + ((mi % 4) * 0.003)));
    const rv = pu * (980 + ((mi % 5) * 60));
    monthly.push({ month: ym, sessions: bs, installs: ins, purchases: pu, all_revenue: rv });
  }

  return { daily, raw, monthly, auDaily };
}

// ═══════════════════════════════════════════════════════
export default function AdjustOverview() {
  const [loading, setLoading] = useState(true);
  const [selectedDateRange, setSelectedDateRange] = useState('30');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);
  const [appliedCustomStart, setAppliedCustomStart] = useState(null);
  const [appliedCustomEnd, setAppliedCustomEnd] = useState(null);

  // Data
  const [dataRows, setDataRows] = useState([]);
  const [dailyAgg, setDailyAgg] = useState([]);
  const [monthlyAll, setMonthlyAll] = useState([]); // always all-time
  const [activeUsersDaily, setActiveUsersDaily] = useState([]);
  const [activeUsersMonthly, setActiveUsersMonthly] = useState([]);

  // UI toggles
  const [grouping, setGrouping] = useState('day');
  const [channelMetric, setChannelMetric] = useState('revenue');
  const [osMetric, setOsMetric] = useState('revenue');
  const [partnerMetric, setPartnerMetric] = useState('revenue');

  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const isDemo = brandId === DEMO_BRAND;

  // ─── Resolve date range ────────────────────────────
  const resolveDates = useCallback(() => {
    const now = new Date();
    switch (selectedDateRange) {
      case 'today': return { s: toYmd(now), e: toYmd(now) };
      case 'yesterday': { const d = new Date(); d.setDate(d.getDate() - 1); return { s: toYmd(d), e: toYmd(d) }; }
      case '7': { const d = new Date(); d.setDate(d.getDate() - 6); return { s: toYmd(d), e: toYmd(now) }; }
      case '30': { const d = new Date(); d.setDate(d.getDate() - 29); return { s: toYmd(d), e: toYmd(now) }; }
      case '90': { const d = new Date(); d.setDate(d.getDate() - 89); return { s: toYmd(d), e: toYmd(now) }; }
      case 'thisMonth': { const d = new Date(now.getFullYear(), now.getMonth(), 1); return { s: toYmd(d), e: toYmd(now) }; }
      case 'lastMonth': {
        const d1 = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const d2 = new Date(now.getFullYear(), now.getMonth(), 0);
        return { s: toYmd(d1), e: toYmd(d2) };
      }
      case 'all': return { s: undefined, e: undefined };
      case 'custom':
        if (appliedCustomStart && appliedCustomEnd) return { s: toYmd(appliedCustomStart), e: toYmd(appliedCustomEnd) };
        return null;
      default: { const d = new Date(); d.setDate(d.getDate() - 29); return { s: toYmd(d), e: toYmd(now) }; }
    }
  }, [selectedDateRange, appliedCustomStart, appliedCustomEnd]);

  // ─── Fetch ─────────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!brandId) return;
    const dates = resolveDates();
    if (!dates) return;
    setLoading(true);
    try {
      const { s, e } = dates;

      // Demo mode
      if (isDemo) {
        const demo = generateDemoData(s, e);
        setDataRows(demo.raw);
        setDailyAgg(demo.daily);
        setMonthlyAll(demo.monthly);
        setActiveUsersDaily(demo.auDaily);
        setActiveUsersMonthly([]);
        return;
      }

      const makeParams = (start, end, extra) => {
        const p = new URLSearchParams();
        if (start) p.set('startDate', start);
        if (end) p.set('endDate', end);
        if (extra) Object.entries(extra).forEach(([k, v]) => p.set(k, v));
        return p.toString();
      };
      const headers = { 'X-Brand-ID': brandId };
      const opts = { credentials: 'include', headers };

      // Parallel fetches
      const [resRaw, resDaily, resMonthly, resAuDaily, resAuMonthly] = await Promise.all([
        fetch(`${API}/adjust/performance?${makeParams(s, e, { limit: '50000' })}`, opts).then(r => r.ok ? r.json() : { rows: [] }),
        fetch(`${API}/adjust/performance/daily?${makeParams(s, e)}`, opts).then(r => r.ok ? r.json() : { rows: [] }),
        // Monthly: always all-time (no date filter)
        fetch(`${API}/adjust/performance/monthly`, opts).then(r => r.ok ? r.json() : { rows: [] }),
        fetch(`${API}/adjust/active-users/daily?${makeParams(s, e)}`, opts).then(r => r.ok ? r.json() : { rows: [] }),
        fetch(`${API}/adjust/active-users/monthly`, opts).then(r => r.ok ? r.json() : { rows: [] }),
      ]);

      setDataRows(Array.isArray(resRaw.rows) ? resRaw.rows : []);
      setDailyAgg(Array.isArray(resDaily.rows) ? resDaily.rows : []);
      setMonthlyAll(Array.isArray(resMonthly.rows) ? resMonthly.rows : []);
      setActiveUsersDaily(Array.isArray(resAuDaily.rows) ? resAuDaily.rows : []);
      setActiveUsersMonthly(Array.isArray(resAuMonthly.rows) ? resAuMonthly.rows : []);
    } catch (err) {
      console.error('Adjust overview fetch error:', err);
      setDataRows([]); setDailyAgg([]); setMonthlyAll([]); setActiveUsersDaily([]); setActiveUsersMonthly([]);
    } finally {
      setLoading(false);
    }
  }, [brandId, isDemo, resolveDates]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── KPI aggregates ────────────────────────────────
  const kpis = useMemo(() => {
    const basis = dailyAgg;
    if (!basis?.length) return { revenue: 0, sessions: 0, purchases: 0, installs: 0, rps: 0, purchaseRate: 0 };
    let revenue = 0, sessions = 0, purchases = 0, installs = 0;
    basis.forEach(r => {
      revenue += Number((r.all_revenue ?? r.revenue) || 0);
      sessions += Number(r.sessions || 0);
      purchases += Number((r.purchases ?? r.purchase_events) || 0);
      installs += Number(r.installs || 0);
    });
    return {
      revenue, sessions, purchases, installs,
      rps: sessions > 0 ? revenue / sessions : 0,
      purchaseRate: sessions > 0 ? (purchases / sessions) * 100 : 0,
    };
  }, [dailyAgg]);

  // ─── Active users latest values ────────────────────
  const auLatest = useMemo(() => {
    if (!activeUsersDaily?.length) return { dau: 0, wau: 0, mau: 0, date: null };
    const last = activeUsersDaily[activeUsersDaily.length - 1];
    return {
      dau: Number(last.daus || 0),
      wau: Number(last.waus || 0),
      mau: Number(last.maus || 0),
      date: last.date,
    };
  }, [activeUsersDaily]);

  const hasActiveUsers = auLatest.dau > 0 || auLatest.wau > 0 || auLatest.mau > 0;

  // ─── Trend data (daily or monthly grouping) ────────
  const byDate = useMemo(() => {
    if (!dailyAgg?.length) return { labels: [], revenue: [], sessions: [], purchases: [], installs: [] };
    const m = new Map();
    dailyAgg.forEach(r => {
      const raw = r.day || r.date;
      if (!raw) return;
      const key = grouping === 'month' ? String(raw).slice(0, 7) : String(raw).slice(0, 10);
      const cur = m.get(key) || { revenue: 0, sessions: 0, purchases: 0, installs: 0 };
      cur.revenue += Number((r.all_revenue ?? r.revenue) || 0);
      cur.sessions += Number(r.sessions || 0);
      cur.purchases += Number((r.purchases ?? r.purchase_events) || 0);
      cur.installs += Number(r.installs || 0);
      m.set(key, cur);
    });
    const entries = Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    let labels = entries.map(e => e[0]);
    if (grouping === 'month') labels = labels.map(ym => monthLabel(ym));
    return {
      labels,
      revenue: entries.map(e => e[1].revenue),
      sessions: entries.map(e => e[1].sessions),
      purchases: entries.map(e => e[1].purchases),
      installs: entries.map(e => e[1].installs),
    };
  }, [dailyAgg, grouping]);

  // ─── Active users trend ────────────────────────────
  const auTrend = useMemo(() => {
    if (!activeUsersDaily?.length) return { labels: [], dau: [], wau: [], mau: [] };
    const sorted = [...activeUsersDaily].sort((a, b) => String(a.date).localeCompare(String(b.date)));
    return {
      labels: sorted.map(r => String(r.date).slice(0, 10)),
      dau: sorted.map(r => Number(r.daus || 0)),
      wau: sorted.map(r => Number(r.waus || 0)),
      mau: sorted.map(r => Number(r.maus || 0)),
    };
  }, [activeUsersDaily]);

  // ─── Dimension aggregation ─────────────────────────
  const getMetricValue = (row, key) => {
    switch (key) {
      case 'revenue': return Number((row.all_revenue ?? row.revenue) || 0);
      case 'sessions': return Number(row.sessions || 0);
      case 'purchases': return Number(row.purchase_events || row.events || 0);
      case 'installs': return Number(row.installs || 0);
      default: return 0;
    }
  };

  const aggregateByDimension = (rows, dimension, metricKey) => {
    const map = new Map();
    rows.forEach(r => {
      let key = 'Unknown';
      if (dimension === 'channel') key = r.channel || r.source_network || 'Unknown';
      else if (dimension === 'os') key = r.os_name || 'Unknown';
      else if (dimension === 'partner') key = r.partner || 'Unknown';
      map.set(key, (map.get(key) || 0) + getMetricValue(r, metricKey));
    });
    const sorted = Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
    const top = sorted.slice(0, 6);
    const rest = sorted.slice(6);
    const othersTotal = rest.reduce((acc, [, v]) => acc + v, 0);
    const labels = top.map(([k]) => k);
    const values = top.map(([, v]) => v);
    if (othersTotal > 0) { labels.push('Others'); values.push(othersTotal); }
    return { labels, values };
  };

  const hasData = dataRows?.length > 0;

  // ─── Monthly summary table data (always all-time) ──
  const monthlySummary = useMemo(() => {
    if (!monthlyAll?.length) return [];
    return monthlyAll.map(r => ({
      month: String(r.month).slice(0, 7),
      sessions: Number(r.sessions || 0),
      installs: Number(r.installs || 0),
      purchases: Number(r.purchases || 0),
      revenue: Number((r.all_revenue ?? r.revenue) || 0),
    })).sort((a, b) => a.month.localeCompare(b.month));
  }, [monthlyAll]);

  // ─── Monthly active users for table ────────────────
  const monthlyAuMap = useMemo(() => {
    const m = new Map();
    (activeUsersMonthly || []).forEach(r => {
      m.set(String(r.month).slice(0, 7), {
        avgDau: Number(r.avg_dau || 0),
        avgWau: Number(r.avg_wau || 0),
        avgMau: Number(r.avg_mau || 0),
      });
    });
    return m;
  }, [activeUsersMonthly]);

  // ═══════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════
  return (
    <div className="p-4 sm:p-6 space-y-6">

      {/* ─── Header ──────────────────────────────── */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-5 rounded-2xl shadow-sm">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
            <img src="/adjustlogopng.png" alt="Adjust" className="w-8 h-8 object-contain" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">Adjust Overview</h1>
            <p className="text-sm text-gray-500 mt-0.5">Mobile attribution & active users at a glance</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select
            className="px-4 py-2 border border-gray-200 rounded-xl text-sm bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[140px]"
            value={selectedDateRange}
            onChange={(e) => {
              setSelectedDateRange(e.target.value);
              if (e.target.value === 'custom') {
                setCustomStartDate(null);
                setCustomEndDate(null);
                setAppliedCustomStart(null);
                setAppliedCustomEnd(null);
              }
            }}
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="90">Last 90 Days</option>
            <option value="thisMonth">This Month</option>
            <option value="lastMonth">Last Month</option>
            <option value="all">All Time</option>
            <option value="custom">Custom Range</option>
          </select>
          {selectedDateRange === 'custom' && (
            <div className="relative">
              <button
                onClick={() => setIsCustomDateOpen(!isCustomDateOpen)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                {customStartDate && customEndDate
                  ? `${customStartDate.toLocaleDateString()} - ${customEndDate.toLocaleDateString()}`
                  : 'Select Date Range'}
              </button>
              {isCustomDateOpen && (
                <div className="absolute right-0 mt-2 z-50 bg-white rounded-xl shadow-lg border border-gray-200 p-4">
                  <DatePicker
                    selected={customStartDate}
                    onChange={([s, e]) => { setCustomStartDate(s); setCustomEndDate(e); }}
                    startDate={customStartDate}
                    endDate={customEndDate}
                    selectsRange inline
                  />
                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        if (customStartDate && customEndDate) {
                          setAppliedCustomStart(customStartDate);
                          setAppliedCustomEnd(customEndDate);
                        }
                        setIsCustomDateOpen(false);
                      }}
                      className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={!customStartDate || !customEndDate}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="flex items-center gap-1 bg-gray-50 p-0.5 rounded-lg">
            {[{ id: 'day', label: 'Daily' }, { id: 'month', label: 'Monthly' }].map(opt => (
              <button
                key={opt.id}
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  grouping === opt.id ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-600 hover:bg-white hover:text-gray-900'
                }`}
                onClick={() => setGrouping(opt.id)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ─── KPI Cards ───────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard label="Revenue" value={fmtCurrency(kpis.revenue)} icon={DollarSign} iconBg="bg-green-50" loading={loading} />
        <KpiCard label="Sessions" value={fmt(kpis.sessions)} icon={MousePointerClick} iconBg="bg-blue-50" loading={loading} />
        <KpiCard label="Purchases" value={fmt(kpis.purchases)} icon={ShoppingCart} iconBg="bg-purple-50" loading={loading} />
        <KpiCard label="Installs" value={fmt(kpis.installs)} icon={Download} iconBg="bg-orange-50" loading={loading} />
        <KpiCard label="Rev / Session" value={fmtCurrency(kpis.rps)} icon={BarChart3} iconBg="bg-cyan-50" loading={loading} />
        <KpiCard label="Purchase Rate" value={fmtPct(kpis.purchaseRate)} icon={Activity} iconBg="bg-pink-50" loading={loading} />
      </div>

      {/* ─── Active Users Cards ──────────────────── */}
      {(hasActiveUsers || loading) && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <ActiveUserCard label="Daily Active Users (DAU)" value={auLatest.dau} color="bg-blue-500" loading={loading}
            sublabel={auLatest.date ? `as of ${String(auLatest.date).slice(0, 10)}` : null} />
          <ActiveUserCard label="Weekly Active Users (WAU)" value={auLatest.wau} color="bg-emerald-500" loading={loading} />
          <ActiveUserCard label="Monthly Active Users (MAU)" value={auLatest.mau} color="bg-purple-500" loading={loading} />
          <ActiveUserCard label="DAU / Net Install" value={kpis.installs > 0 ? (auLatest.dau / kpis.installs).toFixed(2) : '—'} color="bg-amber-500" loading={loading}
            sublabel="Retention efficiency" />
          <ActiveUserCard label="MAU / Net Install" value={kpis.installs > 0 ? (auLatest.mau / kpis.installs).toFixed(2) : '—'} color="bg-rose-500" loading={loading}
            sublabel="Cumulative stickiness" />
        </div>
      )}

      {/* ─── Monthly Summary Table (All-Time) ───── */}
      <Card className="p-5 rounded-2xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-gray-800">Monthly Summary</h3>
            <p className="text-xs text-gray-400 mt-0.5">All-time monthly breakdown — independent of date filter</p>
          </div>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-400"><Loader2 className="w-8 h-8 animate-spin" /></div>
        ) : monthlySummary.length === 0 ? (
          <p className="text-sm text-gray-500 py-8 text-center">No monthly data available.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left p-2.5 font-medium text-gray-500 text-xs uppercase tracking-wide sticky left-0 bg-white">Metric</th>
                  {monthlySummary.map(m => (
                    <th key={m.month} className="text-right p-2.5 font-medium text-gray-500 text-xs uppercase tracking-wide whitespace-nowrap">
                      {monthLabel(m.month)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {[
                  { key: 'revenue', label: 'Revenue', format: fmtCurrency },
                  { key: 'sessions', label: 'Sessions', format: fmt },
                  { key: 'purchases', label: 'Purchases', format: fmt },
                  { key: 'installs', label: 'Installs', format: fmt },
                  { key: 'conversionRate', label: 'Conversion Rate', format: null },
                  { key: 'rps', label: 'Rev / Session', format: null },
                ].map(metric => (
                  <tr key={metric.key} className="hover:bg-gray-50/50">
                    <td className="p-2.5 text-gray-700 font-medium sticky left-0 bg-white whitespace-nowrap">{metric.label}</td>
                    {monthlySummary.map(m => {
                      let val;
                      if (metric.key === 'conversionRate') {
                        val = m.sessions > 0 ? fmtPct((m.purchases / m.sessions) * 100) : '0.00%';
                      } else if (metric.key === 'rps') {
                        val = m.sessions > 0 ? fmtCurrency(m.revenue / m.sessions) : '₺0';
                      } else {
                        val = metric.format(m[metric.key]);
                      }
                      return (
                        <td key={m.month} className="p-2.5 text-right text-gray-800 whitespace-nowrap tabular-nums">{val}</td>
                      );
                    })}
                  </tr>
                ))}
                {/* Active users rows if available */}
                {monthlyAuMap.size > 0 && (
                  <>
                    <tr className="hover:bg-gray-50/50 border-t-2 border-gray-200">
                      <td className="p-2.5 text-gray-700 font-medium sticky left-0 bg-white whitespace-nowrap">Avg DAU</td>
                      {monthlySummary.map(m => (
                        <td key={m.month} className="p-2.5 text-right text-gray-800 whitespace-nowrap tabular-nums">
                          {fmt(monthlyAuMap.get(m.month)?.avgDau || 0)}
                        </td>
                      ))}
                    </tr>
                    <tr className="hover:bg-gray-50/50">
                      <td className="p-2.5 text-gray-700 font-medium sticky left-0 bg-white whitespace-nowrap">Avg WAU</td>
                      {monthlySummary.map(m => (
                        <td key={m.month} className="p-2.5 text-right text-gray-800 whitespace-nowrap tabular-nums">
                          {fmt(monthlyAuMap.get(m.month)?.avgWau || 0)}
                        </td>
                      ))}
                    </tr>
                    <tr className="hover:bg-gray-50/50">
                      <td className="p-2.5 text-gray-700 font-medium sticky left-0 bg-white whitespace-nowrap">Avg MAU</td>
                      {monthlySummary.map(m => (
                        <td key={m.month} className="p-2.5 text-right text-gray-800 whitespace-nowrap tabular-nums">
                          {fmt(monthlyAuMap.get(m.month)?.avgMau || 0)}
                        </td>
                      ))}
                    </tr>
                    <tr className="hover:bg-gray-50/50 border-t border-dashed border-gray-200">
                      <td className="p-2.5 text-gray-700 font-medium sticky left-0 bg-white whitespace-nowrap">DAU / Install</td>
                      {monthlySummary.map(m => {
                        const dau = monthlyAuMap.get(m.month)?.avgDau || 0;
                        const inst = m.installs || 0;
                        return (
                          <td key={m.month} className="p-2.5 text-right text-gray-800 whitespace-nowrap tabular-nums">
                            {inst > 0 ? (dau / inst).toFixed(2) : '—'}
                          </td>
                        );
                      })}
                    </tr>
                    <tr className="hover:bg-gray-50/50">
                      <td className="p-2.5 text-gray-700 font-medium sticky left-0 bg-white whitespace-nowrap">MAU / Install</td>
                      {monthlySummary.map(m => {
                        const mau = monthlyAuMap.get(m.month)?.avgMau || 0;
                        const inst = m.installs || 0;
                        return (
                          <td key={m.month} className="p-2.5 text-right text-gray-800 whitespace-nowrap tabular-nums">
                            {inst > 0 ? (mau / inst).toFixed(2) : '—'}
                          </td>
                        );
                      })}
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ─── Trend Charts ────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5 rounded-2xl">
          <h3 className="font-semibold text-gray-800 mb-4">Revenue Trend</h3>
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 className="w-8 h-8 animate-spin" /></div>
          ) : (
            <LineChart
              height={300}
              data={{
                labels: byDate.labels,
                datasets: [{
                  label: 'Revenue',
                  data: byDate.revenue,
                  borderColor: '#111827',
                  backgroundColor: 'rgba(17,24,39,0.08)',
                  fill: true,
                  tension: 0.3,
                  pointRadius: byDate.labels.length > 60 ? 0 : 3,
                }],
              }}
              options={{
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: (ctx) => `Revenue: ${fmtCurrency(ctx.raw)}`,
                    },
                  },
                },
                scales: {
                  y: { ticks: { callback: (v) => '₺' + (v >= 1000000 ? (v / 1000000).toFixed(1) + 'M' : v >= 1000 ? (v / 1000).toFixed(0) + 'K' : v) } },
                },
              }}
            />
          )}
        </Card>

        <Card className="p-5 rounded-2xl">
          <h3 className="font-semibold text-gray-800 mb-4">Sessions vs Purchases</h3>
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 className="w-8 h-8 animate-spin" /></div>
          ) : (
            <LineChart
              height={300}
              data={{
                labels: byDate.labels,
                datasets: [
                  {
                    label: 'Sessions',
                    data: byDate.sessions,
                    borderColor: 'rgba(99,102,241,1)',
                    backgroundColor: 'rgba(99,102,241,0.1)',
                    fill: true, tension: 0.3,
                    pointRadius: byDate.labels.length > 60 ? 0 : 3,
                    yAxisID: 'y',
                  },
                  {
                    label: 'Purchases',
                    data: byDate.purchases,
                    borderColor: 'rgba(16,185,129,1)',
                    backgroundColor: 'rgba(16,185,129,0.1)',
                    fill: true, tension: 0.3,
                    pointRadius: byDate.labels.length > 60 ? 0 : 3,
                    yAxisID: 'y1',
                  },
                ],
              }}
              options={{
                scales: {
                  y: { position: 'left', title: { display: true, text: 'Sessions' } },
                  y1: { position: 'right', grid: { drawOnChartArea: false }, title: { display: true, text: 'Purchases' } },
                },
              }}
            />
          )}
        </Card>
      </div>

      {/* ─── Active Users Trend ──────────────────── */}
      {(hasActiveUsers || loading) && (
        <Card className="p-5 rounded-2xl">
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-gray-800">Active Users Trend</h3>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 className="w-8 h-8 animate-spin" /></div>
          ) : (
            <LineChart
              height={300}
              data={{
                labels: auTrend.labels,
                datasets: [
                  {
                    label: 'DAU',
                    data: auTrend.dau,
                    borderColor: '#3b82f6',
                    backgroundColor: 'rgba(59,130,246,0.08)',
                    fill: false, tension: 0.3,
                    pointRadius: auTrend.labels.length > 60 ? 0 : 2,
                  },
                  {
                    label: 'WAU',
                    data: auTrend.wau,
                    borderColor: '#10b981',
                    backgroundColor: 'rgba(16,185,129,0.08)',
                    fill: false, tension: 0.3,
                    pointRadius: auTrend.labels.length > 60 ? 0 : 2,
                  },
                  {
                    label: 'MAU',
                    data: auTrend.mau,
                    borderColor: '#a855f7',
                    backgroundColor: 'rgba(168,85,247,0.08)',
                    fill: false, tension: 0.3,
                    pointRadius: auTrend.labels.length > 60 ? 0 : 2,
                  },
                ],
              }}
              options={{
                scales: {
                  y: {
                    ticks: { callback: (v) => v >= 1000000 ? (v / 1000000).toFixed(1) + 'M' : v >= 1000 ? (v / 1000).toFixed(0) + 'K' : v },
                  },
                },
              }}
            />
          )}
        </Card>
      )}

      {/* ─── Distribution Charts ─────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {[
          { title: 'Channel Breakdown', dimension: 'channel', metric: channelMetric, setMetric: setChannelMetric },
          { title: 'OS Breakdown', dimension: 'os', metric: osMetric, setMetric: setOsMetric },
          { title: 'Partner Breakdown', dimension: 'partner', metric: partnerMetric, setMetric: setPartnerMetric },
        ].map(({ title, dimension, metric, setMetric }) => (
          <Card key={dimension} className="p-5 rounded-2xl">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800 text-sm">{title}</h3>
              <select
                className="px-2 py-1 border border-gray-200 rounded-lg text-xs bg-white"
                value={metric}
                onChange={(e) => setMetric(e.target.value)}
              >
                <option value="revenue">Revenue</option>
                <option value="sessions">Sessions</option>
                <option value="purchases">Purchases</option>
                <option value="installs">Installs</option>
              </select>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-12 text-gray-400"><Loader2 className="w-8 h-8 animate-spin" /></div>
            ) : !hasData ? (
              <p className="text-sm text-gray-500 py-12 text-center">No data</p>
            ) : (() => {
              const { labels, values } = aggregateByDimension(dataRows, dimension, metric);
              return (
                <DoughnutChart
                  data={{
                    labels,
                    datasets: [{ data: values, backgroundColor: labels.map((_, i) => palette[i % palette.length]) }],
                  }}
                  options={{
                    plugins: {
                      legend: { position: 'bottom', labels: { boxWidth: 12, padding: 12, font: { size: 11 } } },
                    },
                  }}
                />
              );
            })()}
          </Card>
        ))}
      </div>

      {/* ─── Top Campaigns Table ─────────────────── */}
      <Card className="p-5 rounded-2xl">
        <h3 className="font-semibold text-gray-800 mb-4">Top Campaigns</h3>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-400"><Loader2 className="w-8 h-8 animate-spin" /></div>
        ) : !hasData ? (
          <p className="text-sm text-gray-500 py-8 text-center">No campaign data available.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left p-2.5 font-medium text-gray-500 text-xs uppercase tracking-wide">Campaign</th>
                  <th className="text-left p-2.5 font-medium text-gray-500 text-xs uppercase tracking-wide">Channel</th>
                  <th className="text-right p-2.5 font-medium text-gray-500 text-xs uppercase tracking-wide">Revenue</th>
                  <th className="text-right p-2.5 font-medium text-gray-500 text-xs uppercase tracking-wide">Sessions</th>
                  <th className="text-right p-2.5 font-medium text-gray-500 text-xs uppercase tracking-wide">Purchases</th>
                  <th className="text-right p-2.5 font-medium text-gray-500 text-xs uppercase tracking-wide">Installs</th>
                  <th className="text-right p-2.5 font-medium text-gray-500 text-xs uppercase tracking-wide">Conv Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(() => {
                  const map = new Map();
                  dataRows.forEach(r => {
                    const key = (r.campaign || 'Unknown').toString();
                    const cur = map.get(key) || { revenue: 0, sessions: 0, purchases: 0, installs: 0, channel: r.channel || r.source_network || '' };
                    cur.revenue += Number(r.all_revenue || 0);
                    cur.sessions += Number(r.sessions || 0);
                    cur.purchases += Number(r.purchase_events || r.events || 0);
                    cur.installs += Number(r.installs || 0);
                    map.set(key, cur);
                  });
                  return Array.from(map.entries())
                    .sort((a, b) => b[1].revenue - a[1].revenue)
                    .slice(0, 15)
                    .map(([name, v], i) => (
                      <tr key={name} className="hover:bg-gray-50/50">
                        <td className="p-2.5 text-gray-800 font-medium max-w-[300px] truncate">{name}</td>
                        <td className="p-2.5 text-gray-500 text-xs">{v.channel}</td>
                        <td className="p-2.5 text-right text-gray-800 tabular-nums">{fmtCurrency(v.revenue)}</td>
                        <td className="p-2.5 text-right text-gray-600 tabular-nums">{fmt(v.sessions)}</td>
                        <td className="p-2.5 text-right text-gray-600 tabular-nums">{fmt(v.purchases)}</td>
                        <td className="p-2.5 text-right text-gray-600 tabular-nums">{fmt(v.installs)}</td>
                        <td className="p-2.5 text-right text-gray-600 tabular-nums">
                          {v.sessions > 0 ? fmtPct((v.purchases / v.sessions) * 100) : '—'}
                        </td>
                      </tr>
                    ));
                })()}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
